import React from "react";
import {
  ArrowRight,
  Sparkles,
  BarChart3,
  Palette,
  UserPlus,
  Crown,
  BookOpen,
  Zap,
  Brush,
  Monitor,
  Cpu,
  Check,
  X as XIcon,
  Rocket,
  PlayCircle,
} from "lucide-react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import { LeadModalProvider, useLeadModal } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import Seo from "@/seo/Seo";

// ---------------------------------------------------------------------------
// Features data
// ---------------------------------------------------------------------------
const FEATURES = [
  {
    key: "influencer-detection",
    eyebrow: "INFLUENCER DETECTION",
    icon: Crown,
    title: "Detect social media influencers.",
    lead:
      "Identify VIP guests automatically. Stori cross-references every lead against Instagram and TikTok in real time — so your team treats your top guests like the marketers they are.",
    bullets: [
      "VIP Guest Alerts pushed to your team's phone the moment they walk in",
      "Influencer Score combines reach, engagement, and audience quality",
      "Full leaderboard sortable by follower count, engagement, and recency",
    ],
    image:
      "/features/influencer-detection.webp",
    accent: "amber",
  },
  {
    key: "ai-transform",
    eyebrow: "AI TRANSFORMATION",
    icon: Sparkles,
    title: "AI transforms your look instantly.",
    lead:
      "One tap. Endless looks. Guests step into the camera once and walk away with a feed-ready hero shot — styled to match your brand, your venue, your moment.",
    bullets: [
      "On-device AI runs in under 2 seconds, no cloud round-trip",
      "Identity-locked — every guest still looks like themselves",
      "Influencer Vault, Sunset Hills Pool, Private Jet — preset packs for every venue",
    ],
    image:
      "/features/ai-transform.webp",
    accent: "cyan",
  },
  {
    key: "real-time-analytics",
    eyebrow: "REAL-TIME ANALYTICS",
    icon: BarChart3,
    title: "Track engagement in real-time.",
    lead:
      "Every share, every scan, every lead — surfaced in a dashboard your team will actually use. Powerful enough for an analyst, simple enough for a GM.",
    bullets: [
      "Live gender, unique vs. repeat, and conversion funnel views",
      "Influencer-detection feed with platform-by-platform follower counts",
      "Export to CSV, push to Salesforce / HubSpot / Klaviyo with one click",
    ],
    image:
      "/features/dashboard.webp",
    accent: "amber",
  },
  {
    key: "tv-display",
    eyebrow: "TV DISPLAY",
    icon: Monitor,
    title: "Showcase moments on every screen at your event.",
    lead:
      "Stori's TV Display turns any television into a live, on-brand gallery wall — guest photos auto-rotate the moment they're captured, with your branding, sponsor lockups, and watermark baked in.",
    bullets: [
      "Plug-and-play on any HDMI or Smart TV — no extra hardware required",
      "Branded auto-rotating slideshow with your logo, colors, and sponsor seals",
      "Engage everyone in the room — even guests who haven't taken a photo yet",
    ],
    image: "/features/tv-display.webp",
    accent: "cyan",
  },
  {
    key: "web-gallery",
    eyebrow: "WEB GALLERY MANAGEMENT",
    icon: Monitor,
    title: "Real-time moderation and sharing controls.",
    lead:
      "Every photo, every video, in one live dashboard — feature, favorite, archive, or delete in a single tap. Push to TVs, send to web galleries, or generate a QR code, all from one screen.",
    bullets: [
      "TV Gallery, Web Gallery, and QR Code outputs — switch with one tap",
      "Live counts: Total Media, Featured, Archived, Images vs. Videos",
      "Auto-rotate slides with built-in moderation — feature the best, archive the rest",
    ],
    image: "/features/web-gallery.webp",
    accent: "amber",
  },
  {
    key: "styles",
    eyebrow: "STYLE LIBRARY",
    icon: Palette,
    title: "Endless styles to choose from.",
    lead:
      "Find the perfect look for every guest. A growing library of AI styles — pre-tuned for hospitality, retail, beauty, and live activations — and your own brand can ship a custom pack in days.",
    bullets: [
      "Influencer Vault, Sunset Beach, City Lights, Studio Editorial, and growing",
      "Bring-your-own brand pack with on-pose, on-identity controls",
      "Favorites + Recents per device — guests pick the look they love",
    ],
    image:
      "/features/styles.webp",
    accent: "cyan",
  },
  {
    key: "lead-capture",
    eyebrow: "LEAD CAPTURE",
    icon: UserPlus,
    title: "Capture leads automatically.",
    lead:
      "Turn fun into valuable connections. Guests trade an email for their photo, your team trades that email for a marketable customer — every single time.",
    bullets: [
      "Branded delivery screen — Powered by Stori, themed for your event",
      "Total Leads, This Event, and Conversion Rate at a glance",
      "Recent Leads feed updates live as guests interact with the booth",
    ],
    image:
      "/features/lead-capture.webp",
    accent: "amber",
  },
  {
    key: "playbooks",
    eyebrow: "PLAYBOOK TEMPLATES",
    icon: BookOpen,
    title: "Launch campaigns in minutes, not hours.",
    lead:
      "Skip the blank page. Stori ships with proven playbook templates for trade shows, product launches, retail activations, festivals, corporate summits, and permanent installs — each one tuned to a specific goal.",
    bullets: [
      "Trade Show Lead Magnet, Product Launch Buzz Builder, Retail Activation Drive, and more",
      "Pre-tagged with goals: Lead Capture, UGC Volume, Brand Awareness, Subscriber Growth",
      "Customize any playbook for your event, then save it as your own template",
    ],
    image: "/features/playbooks.webp",
    accent: "cyan",
  },
  {
    key: "automate",
    eyebrow: "MARKETING AUTOMATION",
    icon: Zap,
    title: "Turn every photo into a follow-up.",
    lead:
      "The moment a guest snaps a photo, the work begins. Stori automatically syncs leads to your CRM, triggers branded follow-ups, and writes the copy for you — so engagement turns into conversion on autopilot.",
    bullets: [
      "Instant Lead Sync to HubSpot, Salesforce, Klaviyo — with auto-tagging",
      "Smart Follow-Up with AI-generated copy personalized to each guest",
      "Next-Day Thank You emails, SMS, and conversion sequences built-in",
    ],
    image: "/features/automate.webp",
    accent: "amber",
  },
  {
    key: "design",
    eyebrow: "DESIGN CUSTOMIZATION",
    icon: Brush,
    title: "Match your brand, perfectly.",
    lead:
      "Every screen, every email, every overlay — fully yours. From the home screen to the SMS that delivers the photo, customize every detail with a live preview so what you see is what your guests get.",
    bullets: [
      "System Branding, Home Screen, Photo & Video, Promo Video, Email, SMS, Media Viewer",
      "Typography, button colors, overlay styles, animation settings — pixel-level control",
      "Real-time preview updates as you edit — no guessing, no deploys",
    ],
    image: "/features/design.webp",
    accent: "cyan",
  },
];

// ---------------------------------------------------------------------------
// 1. Video hero — opens the page with a cinematic embed
// ---------------------------------------------------------------------------
const HERO_VIDEO_ID = "YOUR_VIDEO_ID"; // ← replace with real YouTube video ID

function VideoHero() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="why-us-video-hero"
      className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] bg-stori-cyan/12 rounded-full blur-[180px] pointer-events-none" />
      <div className="absolute inset-0 grid-noise opacity-30 radial-mask pointer-events-none" />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          WHY US
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h1
          data-testid="why-us-h1"
          className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white max-w-5xl mx-auto"
        >
          We invented the AI selfie cam.<br />
          <span className="text-stori-cyan cyan-text-glow">
            Then we kept inventing.
          </span>
        </h1>
        <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl mx-auto leading-relaxed">
          A decade of shipping under one roof. The platform behind 25M+ guest
          interactions and 10,000+ events — see it in motion.
        </p>
      </div>

      {/* Cinematic 16:9 video embed */}
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 mt-14 lg:mt-16">
        <div
          className="relative rounded-3xl overflow-hidden border border-stori-cyan/30 bg-gradient-to-b from-stori-cyan/10 to-transparent p-2 lg:p-3 shadow-[0_30px_120px_rgba(0,229,255,0.15)]"
          data-testid="why-us-video-frame"
        >
          <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
            <iframe
              title="Why selfiecam.ai — product overview"
              src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?rel=0&modestbranding=1`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
            />
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="why-us-hero-demo"
            className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:-translate-y-0.5"
          >
            Book a demo
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            type="button"
            onClick={() => openModal("trial_signup")}
            data-testid="why-us-hero-trial"
            className="inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors font-medium"
          >
            <PlayCircle className="w-4 h-4" />
            Start your free trial
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. "We ship new features every week" — pulsing banner
// ---------------------------------------------------------------------------
function WeeklyShipBanner() {
  return (
    <section
      data-testid="weekly-ship-banner"
      className="relative py-10 lg:py-14 border-y border-stori-border bg-stori-surface1/40 overflow-hidden"
    >
      <div className="absolute inset-0 grid-noise opacity-20 pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-center gap-5 md:gap-8 text-center md:text-left">
        {/* Pulsing dot */}
        <span className="relative inline-flex items-center justify-center w-3 h-3 flex-shrink-0">
          <span className="absolute inset-0 rounded-full bg-stori-cyan/40 pulse-ring" />
          <span className="relative inline-block w-3 h-3 rounded-full bg-stori-cyan shadow-[0_0_18px_rgba(0,229,255,0.7)]" />
        </span>

        <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-stori-cyan">
          Shipping · Now
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight leading-tight">
          We ship new features <span className="text-stori-cyan cyan-text-glow">every week.</span>
        </h2>

        <div className="hidden md:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-stori-muted">
          <Rocket className="w-3.5 h-3.5 text-stori-cyan" />
          Always evolving · Never stagnant
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3. Us vs Them — bold side-by-side comparison
// ---------------------------------------------------------------------------
const VS_ROWS = [
  { label: "Invented the AI selfie cam (2013)", us: true, them: false },
  { label: "Real-time influencer detection", us: true, them: false },
  { label: "On-device AI · sub-2-second transforms", us: true, them: false },
  { label: "Native CRM sync · HubSpot, Salesforce, Klaviyo", us: true, them: "Zapier add-on" },
  { label: "Hardware-agnostic · use your own gear", us: true, them: false },
  { label: "Permanent installs + event rentals", us: true, them: "Events only" },
  { label: "Marketing playbooks built-in", us: true, them: false },
  { label: "Transparent pricing", us: true, them: false },
  { label: "New features shipped weekly", us: true, them: false },
  { label: "Dedicated event producer", us: true, them: "Extra fee" },
];

function UsVsThem() {
  return (
    <section
      data-testid="us-vs-them"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
            <span className="inline-block h-px w-10 bg-stori-cyan" />
            US VS THEM
            <span className="inline-block h-px w-10 bg-stori-cyan" />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
            Same category.{" "}
            <span className="text-stori-cyan cyan-text-glow">Different league.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg text-stori-muted leading-relaxed">
            We don&apos;t just check more boxes. We invented half of them.
          </p>
        </div>

        <div className="mt-14 lg:mt-16 grid lg:grid-cols-2 gap-5 lg:gap-6">
          {/* Us column */}
          <div className="relative rounded-3xl border border-stori-cyan/50 bg-gradient-to-b from-stori-cyan/10 to-transparent p-7 lg:p-9 shadow-[0_0_60px_rgba(0,229,255,0.18)]">
            <div className="absolute -top-3 left-9 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stori-cyan text-black font-mono text-[10px] uppercase tracking-[0.22em] font-semibold">
              <Sparkles className="w-3 h-3" />
              selfiecam.ai
            </div>
            <h3 className="mt-4 font-heading text-2xl lg:text-3xl font-semibold text-white tracking-tight">
              The platform built by the original inventors.
            </h3>
            <ul className="mt-7 space-y-3">
              {VS_ROWS.map((r, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm lg:text-base text-white"
                >
                  <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-stori-cyan/15 text-stori-cyan flex-shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="leading-relaxed">{r.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Them column */}
          <div className="relative rounded-3xl border border-stori-border bg-stori-surface1/30 p-7 lg:p-9 grayscale-[40%]">
            <div className="absolute -top-3 left-9 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stori-surface2 text-stori-muted font-mono text-[10px] uppercase tracking-[0.22em] font-semibold border border-stori-border">
              Other tools
            </div>
            <h3 className="mt-4 font-heading text-2xl lg:text-3xl font-semibold text-stori-muted tracking-tight">
              The category followers.
            </h3>
            <ul className="mt-7 space-y-3">
              {VS_ROWS.map((r, i) => {
                const isText = typeof r.them === "string";
                return (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm lg:text-base text-stori-muted/80"
                  >
                    {isText ? (
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/10 text-amber-300/80 flex-shrink-0 text-[9px] font-mono">
                        ~
                      </span>
                    ) : (
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border border-stori-border text-stori-muted/60 flex-shrink-0">
                        <XIcon className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                    )}
                    <span className="leading-relaxed">
                      {isText ? (
                        <>
                          <span className="text-stori-muted/60 line-through decoration-stori-border mr-2">
                            {r.label}
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300/80">
                            {r.them}
                          </span>
                        </>
                      ) : (
                        <span className="text-stori-muted/60 line-through decoration-stori-border">
                          {r.label}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hardware-agnostic block (demoted from H1 → H2)
// ---------------------------------------------------------------------------
function HardwareOpener() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="hardware-opener"
      className="relative py-24 lg:py-32 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          HARDWARE-AGNOSTIC
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white max-w-4xl mx-auto leading-[1.05]">
          Choose Your Own <span className="text-stori-cyan cyan-text-glow">Hardware.</span>
        </h2>
        <p className="mt-7 text-base lg:text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          No hardware sales. No ecosystem lock-in. Our software is built to
          elevate the equipment you already own — or want to buy. Slip it into
          any kiosk, vintage shell, or roaming rig. It just works.
        </p>
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="hardware-recommendations-cta"
            className="group inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan font-medium px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors"
          >
            <Cpu className="w-4 h-4" />
            Hardware Recommendations
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Features intro (kept, reframed as "What you'll find inside")
// ---------------------------------------------------------------------------
function FeaturesIntro() {
  return (
    <section
      data-testid="features-intro"
      className="relative pt-24 lg:pt-32 pb-4 border-t border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          WHAT&apos;S INSIDE
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] max-w-4xl mx-auto">
          Ten reasons{" "}
          <span className="text-stori-cyan cyan-text-glow">it&apos;s not even close.</span>
        </h2>
        <p className="mt-7 text-base lg:text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          Every capability below is built in-house, refined across 10,000+ live
          events, and shipped under one roof — and a new one lands every week.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
function FeaturesHero() {
  return (
    <section
      data-testid="features-hero"
      className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 grid-noise opacity-30 radial-mask pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          FEATURES
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h1
          data-testid="features-h1"
          className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white max-w-4xl mx-auto"
        >
          The features competitors<br />
          <span className="text-stori-cyan cyan-text-glow">wish they could ship.</span>
        </h1>
        <p className="mt-8 text-lg lg:text-xl text-stori-muted max-w-2xl mx-auto leading-relaxed">
          We invented the AI selfie cam in 2013 — and we&apos;ve been a decade
          ahead ever since. The AI engine, the dashboards, the playbooks,
          the influencer detection — every Stori capability is built in-house,
          refined across 10,000+ live events, and shipped under one roof. No
          bolt-ons. No copycats. No close second.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Feature row
// ---------------------------------------------------------------------------
function FeatureRow({ feature, index }) {
  const Icon = feature.icon;
  const reverse = index % 2 === 1;
  const accentText = feature.accent === "amber" ? "text-amber-300" : "text-stori-cyan";
  const accentBorder = feature.accent === "amber" ? "border-amber-400/30" : "border-stori-cyan/30";
  const accentGlow =
    feature.accent === "amber"
      ? "from-amber-500/15 to-transparent"
      : "from-stori-cyan/15 to-transparent";

  return (
    <section
      data-testid={`feature-row-${feature.key}`}
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div
          className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
            reverse ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {/* Image */}
          <div
            className={`relative rounded-3xl overflow-hidden border ${accentBorder} bg-gradient-to-b ${accentGlow} p-2 lg:p-3`}
          >
            <div className="relative rounded-2xl overflow-hidden bg-stori-bg">
              <img
                src={feature.image}
                alt={feature.title}
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
          </div>

          {/* Copy */}
          <div>
            <div className={`label-eyebrow flex items-center gap-2 mb-5 ${accentText}`}>
              <span className={`inline-block h-px w-8 bg-current`} />
              {String(index + 1).padStart(2, "0")} · {feature.eyebrow}
            </div>

            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.08]">
              {feature.title}
            </h2>

            <p className="mt-6 text-base lg:text-lg text-stori-muted leading-relaxed">
              {feature.lead}
            </p>

            <ul className="mt-8 space-y-4">
              {feature.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm lg:text-base text-white">
                  <span
                    className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-stori-surface1 border ${accentBorder} ${accentText} flex-shrink-0`}
                  >
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 inline-flex items-center gap-3 text-stori-muted text-xs font-mono uppercase tracking-[0.22em]">
              <Icon className={`w-3.5 h-3.5 ${accentText}`} />
              <span>Built-in · No extra setup required</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CTA
// ---------------------------------------------------------------------------
function FeaturesCTA() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="features-cta"
      className="relative py-24 lg:py-32 overflow-hidden bg-stori-bg"
    >
      <div className="absolute inset-0 grid-noise opacity-40 radial-mask pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-stori-cyan/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          STILL DECIDING?
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
          See for yourself{" "}
          <span className="text-stori-cyan cyan-text-glow">
            why it&apos;s not even close.
          </span>
        </h2>
        <p className="mt-6 text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          A 20-minute live walkthrough — every screen, every playbook, and the
          exact ROI calculator we ship with every install.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="features-cta-demo"
            className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:-translate-y-0.5"
          >
            Book a demo
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            type="button"
            onClick={() => openModal("trial_signup")}
            data-testid="features-cta-trial"
            className="inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors font-medium"
          >
            Get started for free
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Features() {
  // ItemList JSON-LD enumerating all 10 capabilities
  const FEATURES_LD = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Stori AI Selfie Cam Features",
    itemListElement: FEATURES.map((f, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: f.title,
      description: f.lead,
    })),
  };
  return (
    <LeadModalProvider>
      <Seo path="/features" jsonLd={[FEATURES_LD]} />
      <main data-testid="why-us-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <VideoHero />
        <WeeklyShipBanner />
        <UsVsThem />
        <HardwareOpener />
        <FeaturesIntro />
        {FEATURES.map((f, i) => (
          <FeatureRow key={f.key} feature={f} index={i} />
        ))}
        <FeaturesCTA />
        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
